// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // Same-origin dev (kgnex.com:3000 via nginx → webpack proxies /api → :3500)
  if (useDevApiServer && process.env.REACT_APP_DEV_API_SAME_ORIGIN === 'true') {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return marketplaceRootURL
      ? marketplaceRootURL.replace(/\/$/, '')
      : `http://127.0.0.1:${port}`;
  }

  // Local dev: browser talks directly to the dev API server on :3500
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Production SSR / built client
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const reportEscrowSuccess = payload => {
  const url = `${apiBaseUrl()}/api/escrow-success`;
  return window.fetch(url, {
    method: methods.POST,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(res => {
    if (!res.ok) {
      return res.json().then(data => {
        const error = new Error(data.error || 'Escrow sync failed');
        throw Object.assign(error, data);
      });
    }
    return res.json();
  });
};

export const fetchTownHallPosts = () => {
  const url = `${apiBaseUrl()}/api/townhall`;
  return window.fetch(url, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then(res => {
    if (!res.ok) {
      return res.json().then(data => {
        const error = new Error(data.error || 'Failed to load Town Hall');
        throw Object.assign(error, data);
      });
    }
    return res.json();
  });
};

export const createTownHallPost = content => {
  const url = `${apiBaseUrl()}/api/townhall`;
  return window.fetch(url, {
    method: methods.POST,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ content }),
  }).then(res => {
    if (!res.ok) {
      return res.json().then(data => {
        const error = new Error(data.error || 'Failed to publish post');
        throw Object.assign(error, data);
      });
    }
    return res.json();
  });
};

const jsonApi = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  return window
    .fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
      ...options,
      body: options.body != null ? JSON.stringify(options.body) : undefined,
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          const error = new Error(data.error || `Request failed (${res.status})`);
          error.status = res.status;
          throw Object.assign(error, data);
        });
      }
      if (res.status === 204) {
        return null;
      }
      return res.json();
    });
};

/** Authenticated tablet job board (Sharetribe session → shadow Postgres). */
export const fetchTabletJobs = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.appId) qs.set('appId', params.appId);
  if (params.kind) qs.set('kind', params.kind);
  const q = qs.toString();
  return jsonApi(`/api/tablet-jobs${q ? `?${q}` : ''}`, { method: methods.GET });
};

export const upsertTabletJob = job =>
  jsonApi(`/api/tablet-jobs/${encodeURIComponent(job.id)}`, {
    method: methods.PUT,
    body: job,
  });

export const migrateTabletJobs = jobs =>
  jsonApi('/api/tablet-jobs/migrate', {
    method: methods.POST,
    body: { jobs },
  });

export const deleteTabletJob = jobId =>
  jsonApi(`/api/tablet-jobs/${encodeURIComponent(jobId)}`, {
    method: methods.DELETE,
  });

export const joinSupplyWaitlist = body =>
  jsonApi('/api/supply-waitlist', {
    method: methods.POST,
    body,
  });

export const fetchMySupplyWaitlist = () =>
  jsonApi('/api/supply-waitlist', { method: methods.GET });

export const fetchLandingListingsFromApi = () => {
  const url = `${apiBaseUrl()}/api/landing-listings`;
  return window.fetch(url, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then(res => {
    if (!res.ok) {
      return res.json().then(data => {
        const error = new Error(data.error || 'Failed to load listings');
        error.status = res.status;
        error.statusText = data.statusText;
        throw error;
      });
    }
    return res.json();
  });
};

export const uploadIpfsEvidence = payload => {
  const url = `${apiBaseUrl()}/api/ipfs-evidence`;
  return window.fetch(url, {
    method: methods.POST,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  }).then(res => {
    if (!res.ok) {
      return res.json().then(data => {
        const error = new Error(data.error || 'IPFS evidence upload failed');
        throw Object.assign(error, data);
      });
    }
    return res.json();
  });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

const parseJsonResponse = async (res, path) => {
  const text = await res.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    const isHtml = /^\s*<(!DOCTYPE|html)/i.test(text);
    const hint = isHtml && res.status === 404
      ? `API route missing (${path}). Rebuild/restart the dev API server.`
      : isHtml
        ? `Server returned HTML instead of JSON (${res.status})`
        : text.slice(0, 160);
    throw new Error(hint);
  }
};

const jsonRequest = (path, { method = 'GET', body } = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  return window.fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async res => {
    const data = await parseJsonResponse(res, path);
    if (!res.ok) {
      const error = new Error(data.error || 'Request failed');
      error.status = res.status;
      error.code = data.code;
      throw Object.assign(error, data);
    }
    return data;
  });
};

export const sendMessageViaApi = body => jsonRequest('/api/messages/send', { method: 'POST', body });

export const blockUser = blockedId =>
  jsonRequest('/api/users/block', { method: 'POST', body: { blocked_id: blockedId } });

export const unblockUser = blockedId =>
  jsonRequest('/api/users/unblock', { method: 'POST', body: { blocked_id: blockedId } });

export const initiateTrade = body =>
  jsonRequest('/api/trade/initiate', { method: 'POST', body });

export const getTradeStatus = transactionId => jsonRequest(`/api/trade/${transactionId}`);

export const verifyTradePin = body =>
  jsonRequest('/api/trade/verify-pin', { method: 'POST', body });

export const raiseTradeDispute = body =>
  jsonRequest('/api/trade/dispute', { method: 'POST', body });

export const notifyProxyArrived = body =>
  jsonRequest('/api/trade/proxy-arrived', { method: 'POST', body });

export const claimProxyGig = (transactionId, { runnerWallet } = {}) =>
  jsonRequest(`/api/proxy/claim-gig/${transactionId}`, {
    method: 'POST',
    body: runnerWallet ? { runner_wallet: runnerWallet } : {},
  });

export const fetchProxyGigContext = transactionId =>
  jsonRequest(`/api/proxy/gig-context/${transactionId}`);

export const markProxyPickedUp = transactionId =>
  jsonRequest(`/api/proxy/mark-picked-up/${transactionId}`, { method: 'POST' });

export const fetchListingLifecycle = listingId =>
  jsonRequest(`/api/listings/${listingId}/lifecycle`);

export const fetchArchivedListingSnapshot = listingId =>
  jsonRequest(`/api/listings/${listingId}/archived-snapshot`);

export const fetchListingQa = listingId => jsonRequest(`/api/listings/${listingId}/qa`);

export const askListingQuestion = (listingId, questionText) =>
  jsonRequest(`/api/listings/${listingId}/ask`, {
    method: 'POST',
    body: { questionText },
  });

export const answerListingQuestion = (listingId, questionId, answerText) =>
  jsonRequest(`/api/listings/${listingId}/answer`, {
    method: 'POST',
    body: { questionId, answerText },
  });

export const fetchNoticeboardFeed = () => jsonRequest('/api/noticeboard/feed');

export const fetchEndUserTradeTracking = () =>
  jsonRequest('/api/noticeboard/end-user-trades');

export const fetchMerchantNoticeboardGigs = ({ merchantId, storeId } = {}) => {
  if (storeId) {
    return jsonRequest(
      `/api/noticeboard/merchant-gigs?store_id=${encodeURIComponent(storeId)}`
    );
  }
  return jsonRequest(
    `/api/noticeboard/merchant-gigs?merchant_id=${encodeURIComponent(merchantId)}`
  );
};

export const fetchMerchantStores = ownerWallet =>
  jsonRequest(`/api/merchant/stores?owner_wallet=${encodeURIComponent(ownerWallet)}`);

export const fetchMerchantStore = storeId =>
  jsonRequest(`/api/merchant/stores/${encodeURIComponent(storeId)}`);

export const createMerchantStore = body =>
  jsonRequest('/api/merchant/stores', { method: 'POST', body });

export const fetchStorefrontBySlug = slug =>
  jsonRequest(`/api/merchant/storefront/slug/${encodeURIComponent(slug)}`);

export const fetchStorefrontGigsBySlug = slug =>
  jsonRequest(`/api/merchant/store/slug/${encodeURIComponent(slug)}/gigs`);

export const fetchMerchantStorefront = merchantId =>
  jsonRequest(`/api/merchant/storefront/${encodeURIComponent(merchantId)}`);

export const upsertMerchantStorefront = body =>
  jsonRequest('/api/merchant/storefront', { method: 'PUT', body });

export const saveMerchantStorefrontPuck = body =>
  jsonRequest('/api/merchant/storefront/save', { method: 'POST', body });

export const updateMerchantStorefrontType = body =>
  jsonRequest('/api/merchant/storefront/type', { method: 'PUT', body });

export const exportMerchantLedgerCsv = ({ storeId, merchantId }) => {
  const params = new URLSearchParams({ store_id: storeId });
  if (merchantId) {
    params.set('merchant_id', merchantId);
  }
  const url = `${apiBaseUrl()}/api/merchant/ledger/export?${params.toString()}`;
  return window.fetch(url, { credentials: 'include' }).then(async response => {
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Export failed');
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `betterxchange-ledger-${storeId.slice(0, 8)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  });
};

export const postNoticeboardGig = (body, storeId) =>
  jsonRequest('/api/noticeboard/post-gig', {
    method: 'POST',
    body: storeId ? { ...body, store_id: storeId } : body,
  });

export const confirmNoticeboardFunding = ({ noticeId, transactionHash }) =>
  jsonRequest('/api/noticeboard/confirm-funding', {
    method: 'POST',
    body: {
      notice_id: noticeId,
      transaction_hash: transactionHash,
    },
  });

export const cancelNoticeboardGig = ({ noticeId, merchantId }) =>
  jsonRequest('/api/noticeboard/cancel-gig', {
    method: 'POST',
    body: {
      notice_id: noticeId,
      merchant_id: merchantId,
    },
  });

export const reportNoticeboardIssue = noticeId =>
  jsonRequest('/api/noticeboard/report-issue', {
    method: 'POST',
    body: { notice_id: noticeId },
  });

const adminJsonRequest = (path, adminSecret, { method = 'GET', body } = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  return window.fetch(url, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${adminSecret}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then(res => {
    if (!res.ok) {
      return res.json().then(data => {
        const error = new Error(data.error || 'Request failed');
        error.status = res.status;
        throw Object.assign(error, data);
      });
    }
    return res.json();
  });
};

export const fetchAdminDisputedGigs = adminSecret =>
  adminJsonRequest('/api/admin/disputed-gigs', adminSecret);

export const resolveAdminDispute = ({ noticeId, resolution, adminSecret }) =>
  adminJsonRequest('/api/admin/resolve-dispute', adminSecret, {
    method: 'POST',
    body: {
      notice_id: noticeId,
      resolution,
    },
  });

export const fetchUserPersonaProfile = () => jsonRequest('/api/user/persona');

export const bootstrapUserPersona = initialPersona =>
  jsonRequest('/api/user/persona/bootstrap', {
    method: 'POST',
    body: { initial_persona: initialPersona },
  });

export const updateUserPersonaProfile = body =>
  jsonRequest('/api/user/persona', { method: 'PUT', body });

export const createTradeIntent = body =>
  jsonRequest('/api/v1/trade-intents', { method: 'POST', body });
